import type { RefObject } from 'react';
import { Button } from '@shared/ui/Button';
import { formatCount, typo } from '@entities/fleet';
import type { RecalcSummary } from '@entities/prc';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { MODELS_GEN, count, formatPercent, formatRub } from '../format';
import { TopList } from './styles';

/**
 * Что пересчиталось после правки: сколько моделей подорожало и подешевело,
 * у кого появилась или ушла ошибка, самые большие изменения цены без НДС.
 */
export function RecalcNotice({
  summary,
  lead,
  onCompare,
  onDismiss,
}: {
  summary: RecalcSummary;
  /** Что сделано: «Цены сохранены». */
  lead: string;
  /** Открыть «Было → стало». */
  onCompare?: () => void;
  onDismiss?: () => void;
}) {
  const parts = [
    `выросла у ${count(summary.increased, MODELS_GEN)}`,
    `снизилась у ${count(summary.decreased, MODELS_GEN)}`,
    `не изменилась у ${count(summary.unchanged, MODELS_GEN)}`,
  ];
  return (
    <Notice $tone={summary.newErrors.length > 0 ? 'warning' : 'success'} role="status">
      <span className="text">
        <strong>{lead}.</strong> {typo(`Моделей пересчитано: ${formatCount(summary.models)}.`)}{' '}
        {typo(`Цена без НДС ${parts.join(', ')}.`)}
        {summary.added > 0 && ` ${typo(`Новых в версии: ${summary.added}.`)}`}
        {summary.newErrors.length > 0 &&
          ` ${typo(`Ошибка расчёта появилась у: ${summary.newErrors.join(', ')}.`)}`}
        {summary.fixedErrors.length > 0 &&
          ` ${typo(`Снова считаются: ${summary.fixedErrors.join(', ')}.`)}`}
      </span>
      {onCompare && (
        <TextButton type="button" onClick={onCompare}>
          Сравнить с действующими ценами
        </TextButton>
      )}
      {onDismiss && (
        <TextButton type="button" onClick={onDismiss} aria-label="Скрыть сводку пересчёта">
          Скрыть
        </TextButton>
      )}
      {summary.top.length > 0 && (
        <TopList aria-label="Самые большие изменения цены без НДС">
          {summary.top.map((c) => (
            <li key={c.model_id}>
              <span className="name">{c.model_name}</span>
              <span className="nums">
                {formatRub(c.before)} → <strong>{formatRub(c.after)}</strong>
                {c.pct !== null && ` · ${formatPercent(c.pct, 2, true)}`}
              </span>
            </li>
          ))}
        </TopList>
      )}
    </Notice>
  );
}

/** 409 при сохранении: кто-то изменил версию раньше. Фокус — на «Перечитать». */
export function ConflictNotice({
  message,
  onReread,
  busy,
  buttonRef,
}: {
  message: string;
  onReread: () => void;
  busy: boolean;
  buttonRef: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <Notice $tone="warning" role="alert">
      <span className="text">
        <strong>{/[.!?…]$/.test(message.trim()) ? message : `${message}.`}</strong>{' '}
        {typo(
          'Перечитайте версию: свежие значения станут основой, ваши правки перенесутся поверх.',
        )}
      </span>
      <Button
        ref={buttonRef}
        type="button"
        $variant="secondary"
        $size="sm"
        disabled={busy}
        onClick={onReread}
      >
        {busy ? 'Перечитываю…' : 'Перечитать'}
      </Button>
    </Notice>
  );
}

/** После «Перечитать»: сколько правок перенесено и где коллега менял то же. */
export function CarriedNotice({
  carried,
  conflicts,
  dropped,
}: {
  carried: number;
  conflicts: number;
  dropped: number;
}) {
  const tail =
    conflicts > 0
      ? `В ${conflicts} ${conflicts === 1 ? 'ячейке' : 'ячейках'} коллега тоже менял значение — они отмечены оранжевой рамкой.`
      : 'Проверьте и сохраните ещё раз.';
  return (
    <Notice $tone="info" role="status">
      <span className="text">
        {typo(`Загружена свежая версия. Перенесено ваших правок: ${carried}. ${tail}`)}
        {dropped > 0 &&
          ` ${typo(`Правок, которые некуда перенести (позицию убрали): ${dropped}.`)}`}
      </span>
    </Notice>
  );
}
